import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Printer,
  Edit,
  Copy,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/auth.store'
import { projectsService } from '@/services/projectsService'
import { ProjectListItem } from '@/api/types'

export const ProjectsList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [projects, setProjects] = useState<ProjectListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')

  const branchId = user?.selectedBranch?.branchId

  // Load projects
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadProjects = async () => {
      try {
        setLoading(true)
        const response = await projectsService.getAllProjects(branchId, pageNumber, pageSize)
        const list = response.data || []
        setProjects(list)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('projects.failedToLoadProjects'))
      } finally {
        setLoading(false)
      }
    }

    loadProjects()
  }, [branchId, pageNumber, pageSize])

  const formatAmount = (amount?: number): string => {
    if (amount === undefined || amount === null) return '0.00'
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  // formatDate function removed - not currently used

  // Filter projects based on search query
  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) {
      return projects
    }

    const query = searchQuery.toLowerCase()
    return projects.filter((project) => {
      const code = project.code || ''
      const name = project.name || project.projectName || ''
      const ref = project.ref || project.reference || ''
      return (
        code.toLowerCase().includes(query) ||
        name.toLowerCase().includes(query) ||
        ref.toLowerCase().includes(query)
      )
    })
  }, [projects, searchQuery])

  // Check if row should be highlighted (red) - if end date has crossed today's date
  const shouldHighlightRow = (project: ProjectListItem): boolean => {
    const endDate = project.endDate
    if (!endDate) return false
    
    try {
      const today = new Date()
      today.setHours(0, 0, 0, 0) // Set to start of day for accurate comparison
      
      const projectEndDate = new Date(endDate)
      projectEndDate.setHours(0, 0, 0, 0)
      
      // Return true if end date has crossed (is before today)
      return projectEndDate < today
    } catch (error) {
      return false
    }
  }

  // Calculate project status based on business logic
  const getProjectStatus = (project: ProjectListItem): string => {
    const projectBilled = project.projectBilled || 0
    const projectDue = project.projectDue || 0
    const projectValue = project.projectValue || 0

    // If Project Due = 0, show "Project Billed"
    if (projectDue === 0) {
      return 'Project Billed'
    }

    // If Project Billed = 0 and Project Due has value, show "Project Due"
    if (projectBilled === 0 && projectDue > 0) {
      return 'Project Due'
    }

    // If Project Billed >= Project Value, show "Project Billed"
    if (projectBilled >= projectValue && projectValue > 0) {
      return 'Project Billed'
    }

    // If Project Due > 0 AND Project Billed > 0 AND Project Billed < Project Value, show "In Progress"
    if (projectDue > 0 && projectBilled > 0 && projectBilled < projectValue) {
      return 'In Progress'
    }

    // Otherwise show existing status or "N/A"
    return project.projectStatus || 'N/A'
  }

  const handleEdit = (projectId: number) => {
    navigate(`/projects/${projectId}`)
  }

  const handleAddNew = () => {
    navigate('/projects/new')
  }

  // handleExportCSV removed - not currently used

  const handleCopy = () => {
    // TODO: Implement copy functionality
    toast.success('Copy functionality will be implemented')
  }

  const handlePrint = () => {
    window.print()
  }

  if (loading && projects.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading projects...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('projects.title')}
        description={t('projects.projectDetails')}
      >
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />
            {t('projects.csv')}
          </Button>
          <Button onClick={handleAddNew}>
            <Plus className="w-4 h-4 mr-2" />
            {t('projects.addNew')}
          </Button>
        </div>
      </PageHeader>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4 items-end mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('projects.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="w-4 h-4 mr-2" />
                {t('projects.copy')}
              </Button>
            </div>
          </div>

          {filteredProjects.length === 0 ? (
            <EmptyState
              title={t('projects.noProjectsFound')}
              description={searchQuery ? t('projects.tryAdjustingSearch') : t('projects.getStarted')}
              action={{
                label: t('projects.addNewProjectAction'),
                onClick: handleAddNew,
              }}
            />
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-primary/5">
                      <TableHead className="h-12 px-3 text-left bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('projects.code')}
                      </TableHead>
                      <TableHead className="h-12 px-3 text-left bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('projects.name')}
                      </TableHead>
                      <TableHead className="h-12 px-3 text-left bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('projects.reference')}
                      </TableHead>
                      <TableHead className="h-12 px-3 text-right bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('projects.projectValue')}
                      </TableHead>
                      <TableHead className="h-12 px-3 text-right bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('projects.projectDue')}
                      </TableHead>
                      <TableHead className="h-12 px-3 text-left bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('projects.status')}
                      </TableHead>
                      <TableHead className="h-12 px-3 text-left bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('projects.active')}
                      </TableHead>
                      <TableHead className="h-12 px-3 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('common.edit')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProjects.map((project) => {
                      const projectId = project.projectId ?? project.id
                      const isHighlighted = shouldHighlightRow(project)
                      return (
                        <TableRow
                          key={projectId}
                          className={`border-b hover:bg-primary/5 transition-colors ${
                            isHighlighted ? 'bg-red-50 dark:bg-red-900/20' : ''
                          }`}
                        >
                          <TableCell className="px-3 py-2 text-sm">
                            {project.code || 'N/A'}
                          </TableCell>
                          <TableCell className="px-3 py-2 text-sm font-medium">
                            {project.name || project.projectName || 'N/A'}
                          </TableCell>
                          <TableCell className="px-3 py-2 text-sm">
                            {project.ref || project.reference || '-'}
                          </TableCell>
                          <TableCell className="px-3 py-2 text-sm text-right">
                            {formatAmount(project.projectValue)}
                          </TableCell>
                          <TableCell className="px-3 py-2 text-sm text-right">
                            {formatAmount(project.projectDue)}
                          </TableCell>
                          <TableCell className="px-3 py-2 text-sm">
                            <Badge variant="secondary">
                              {getProjectStatus(project)}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-3 py-2 text-sm">
                            {project.isActive === true || project.isActive === 'Yes' ? (
                              <Badge variant="default">Yes</Badge>
                            ) : (
                              <Badge variant="secondary">No</Badge>
                            )}
                          </TableCell>
                          <TableCell className="px-3 py-2 text-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(projectId!)}
                              className="h-8 w-8"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Showing {filteredProjects.length > 0 ? (pageNumber - 1) * pageSize + 1 : 0} to{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} entries
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
                    disabled={pageNumber === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" disabled>
                    {pageNumber}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.min(totalPages, prev + 1))}
                    disabled={pageNumber >= totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

